/**
 * Canonical white-label design-token palette.
 *
 * This is the SINGLE SOURCE OF TRUTH for the platform's default colors on the
 * frontend. Every themable color in the app should resolve to one of these
 * tokens via a CSS custom property (e.g. `var(--primary)`), so that an agency
 * override (fetched at runtime by company) can re-skin the whole product by
 * swapping the values on `:root`.
 *
 * Token key === CSS variable name without the leading `--`.
 * e.g. token `"primary"` -> CSS var `--primary` -> Tailwind utility `bg-primary`
 *      token `"primary-hover"` -> CSS var `--primary-hover`
 *
 * The backend keeps its own copy of the token KEYS (to validate a write) and
 * the default VALUES (so its API can return an effective theme to a caller that
 * is not this bundle). Two copies is one more than ideal, and unavoidable: the
 * browser needs the defaults before it can make a request.
 *
 * ⚠️ `tests/themeTokens.test.ts` reads `backend/src/app_routes/shell/themeTokens.js`
 * and asserts the keys and values match this file. Add a token in one place and
 * that test fails — which is the point.
 */

export type ThemeTokenKey =
  | "primary"
  | "primary-hover"
  | "primary-foreground"
  | "secondary"
  | "secondary-foreground"
  | "accent"
  | "accent-foreground"
  | "background"
  | "surface"
  | "surface-foreground"
  | "foreground"
  | "muted"
  | "muted-foreground"
  | "border"
  | "input"
  | "ring"
  | "success"
  | "success-foreground"
  | "warning"
  | "warning-foreground"
  | "destructive"
  | "destructive-foreground";

export type ThemeTokens = Record<ThemeTokenKey, string>;
export type PartialThemeTokens = Partial<ThemeTokens>;

/**
 * The default palette. Deliberately neutral — an agency that white-labels this
 * will replace it, and a starter template that ships with somebody else's brand
 * colours is a starter template that ships a bug for the first clone that
 * forgets to change them.
 *
 * These are the colours EVERY agency and sub-account sees until an agency
 * overrides them.
 */
export const DEFAULT_THEME: ThemeTokens = {
  primary: "#3B5BDB",
  "primary-hover": "#2F4AC0",
  "primary-foreground": "#FFFFFF",
  secondary: "#F2F4F7",
  "secondary-foreground": "#101828",
  accent: "#4C6EF5",
  "accent-foreground": "#FFFFFF",
  background: "#F5F6F8",
  surface: "#FFFFFF",
  "surface-foreground": "#101828",
  foreground: "#101828",
  muted: "#F2F4F7",
  "muted-foreground": "#5D6676",
  border: "#E6E8EC",
  input: "#D0D5DD",
  ring: "#3B5BDB",
  success: "#067647",
  "success-foreground": "#FFFFFF",
  warning: "#B54708",
  "warning-foreground": "#FFFFFF",
  destructive: "#B42318",
  "destructive-foreground": "#FFFFFF",
};

export const THEME_TOKEN_KEYS = Object.keys(DEFAULT_THEME) as ThemeTokenKey[];

export interface TokenMeta {
  key: ThemeTokenKey;
  label: string;
  description: string;
}

export interface TokenGroup {
  id: string;
  label: string;
  description: string;
  tokens: TokenMeta[];
}

/**
 * Grouping + human copy used to render the branding editor. Grouped sensibly so
 * an agency can reason about what each color affects.
 */
export const TOKEN_GROUPS: TokenGroup[] = [
  {
    id: "brand",
    label: "Brand",
    description: "Primary action color used for buttons, links and highlights.",
    tokens: [
      { key: "primary", label: "Primary", description: "Main brand color (buttons, active states)." },
      { key: "primary-hover", label: "Primary (hover)", description: "Hover/pressed shade of the primary color." },
      { key: "primary-foreground", label: "Primary text", description: "Text/icon color shown on top of primary." },
    ],
  },
  {
    id: "secondary",
    label: "Secondary & Accent",
    description: "Supporting colors for secondary buttons and accents.",
    tokens: [
      { key: "secondary", label: "Secondary", description: "Secondary surface/button background." },
      { key: "secondary-foreground", label: "Secondary text", description: "Text/icon color on secondary." },
      { key: "accent", label: "Accent", description: "Accent color for emphasis and decorative details." },
      { key: "accent-foreground", label: "Accent text", description: "Text/icon color on accent." },
    ],
  },
  {
    id: "surfaces",
    label: "Surfaces & Text",
    description: "Page background, cards and the main body text color.",
    tokens: [
      { key: "background", label: "Background", description: "App/page background color." },
      { key: "surface", label: "Surface / card", description: "Cards, panels and raised surfaces." },
      { key: "surface-foreground", label: "Surface text", description: "Text color inside cards/surfaces." },
      { key: "foreground", label: "Body text", description: "Default body text color." },
    ],
  },
  {
    id: "neutrals",
    label: "Neutrals & Borders",
    description: "Muted backgrounds, secondary text, borders and focus rings.",
    tokens: [
      { key: "muted", label: "Muted", description: "Subtle background for muted areas." },
      { key: "muted-foreground", label: "Muted text", description: "Secondary/placeholder text color." },
      { key: "border", label: "Border", description: "Default border/divider color." },
      { key: "input", label: "Input border", description: "Form input border color." },
      { key: "ring", label: "Focus ring", description: "Keyboard focus outline color." },
    ],
  },
  {
    id: "status",
    label: "Status",
    description: "Success, warning and error colors used for feedback.",
    tokens: [
      { key: "success", label: "Success", description: "Positive/confirmation color." },
      { key: "success-foreground", label: "Success text", description: "Text/icon on success." },
      { key: "warning", label: "Warning", description: "Caution/attention color." },
      { key: "warning-foreground", label: "Warning text", description: "Text/icon on warning." },
      { key: "destructive", label: "Error", description: "Destructive/error color." },
      { key: "destructive-foreground", label: "Error text", description: "Text/icon on error." },
    ],
  },
];

/** `"primary"` -> `"--primary"` */
export const cssVarName = (key: string): string => `--${key}`;

/**
 * Accepts the common CSS color formats the editor can produce or an agency might
 * paste: #rgb / #rgba / #rrggbb / #rrggbbaa, rgb()/rgba(), hsl()/hsla().
 */
const HEX_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const RGB_RE = /^rgba?\(\s*[\d.]+%?\s*[, ]\s*[\d.]+%?\s*[, ]\s*[\d.]+%?\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i;
const HSL_RE = /^hsla?\(\s*[\d.]+(?:deg|rad|turn)?\s*[, ]\s*[\d.]+%\s*[, ]\s*[\d.]+%\s*(?:[,/]\s*[\d.]+%?\s*)?\)$/i;

export function isValidColor(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  return HEX_RE.test(v) || RGB_RE.test(v) || HSL_RE.test(v);
}

export function isThemeTokenKey(key: string): key is ThemeTokenKey {
  return Object.prototype.hasOwnProperty.call(DEFAULT_THEME, key);
}

/**
 * Merge an agency's (partial, possibly dirty) overrides over the platform
 * defaults. Unknown keys and invalid colors are ignored so a bad row can never
 * break the UI.
 */
export function resolveTheme(overrides?: PartialThemeTokens | null): ThemeTokens {
  const resolved: ThemeTokens = { ...DEFAULT_THEME };
  if (!overrides) return resolved;
  for (const key of THEME_TOKEN_KEYS) {
    const value = overrides[key];
    if (isValidColor(value)) resolved[key] = value;
  }
  return resolved;
}

/** Build a `{ "--primary": "#...", ... }` object for inline `style` / `:root`. */
export function buildCssVars(tokens: ThemeTokens): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const key of THEME_TOKEN_KEYS) {
    vars[cssVarName(key)] = tokens[key];
  }
  return vars;
}

/** Apply resolved tokens as CSS custom properties on a target element. */
export function applyThemeVars(
  tokens: ThemeTokens,
  target?: HTMLElement | null,
): void {
  const el = target ?? (typeof document !== "undefined" ? document.documentElement : null);
  if (!el) return;
  for (const key of THEME_TOKEN_KEYS) {
    el.style.setProperty(cssVarName(key), tokens[key]);
  }
}

/**
 * Token key → i18n slug.
 *
 * The message catalogue uses camelCase (`branding.token.primaryHover`) while
 * the token keys are kebab (`primary-hover`), so the two need a map. It lives
 * here rather than in the branding tab because the onboarding editor renders
 * the same swatches and must label them identically — two maps would drift the
 * moment a token is added.
 */
const TOKEN_I18N: Record<string, string> = {
  primary: "primary",
  "primary-hover": "primaryHover",
  "primary-foreground": "primaryForeground",
  secondary: "secondary",
  "secondary-foreground": "secondaryForeground",
  accent: "accent",
  "accent-foreground": "accentForeground",
  background: "background",
  surface: "surface",
  "surface-foreground": "surfaceForeground",
  foreground: "foreground",
  muted: "muted",
  "muted-foreground": "mutedForeground",
  border: "border",
  input: "input",
  ring: "ring",
  success: "success",
  "success-foreground": "successForeground",
  warning: "warning",
  "warning-foreground": "warningForeground",
  destructive: "destructive",
  "destructive-foreground": "destructiveForeground",
};

export function tokenLabelKey(key: string): string {
  return `branding.token.${TOKEN_I18N[key] || key}`;
}

export function tokenDescKey(key: string): string {
  return `branding.token.${TOKEN_I18N[key] || key}.desc`;
}
